import React, { useState, useEffect, useRef, useCallback } from 'react';

const Testimonials = ({ testimonials = [], variant = 'sa' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const autoPlayRef = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (variant === 'ct') {
        if (e.key === 'ArrowRight') {
          nextSlide();
          setIsPlaying(false); // pause on interaction
        } else if (e.key === 'ArrowLeft') {
          prevSlide();
          setIsPlaying(false); // pause on interaction
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [variant, nextSlide, prevSlide]);

  // Autoplay effect for Corporate Tax variant
  useEffect(() => {
    if (variant === 'ct' && isPlaying) {
      autoPlayRef.current = setInterval(nextSlide, 7000); // 7s rotation matching typical layouts
    } else {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [variant, isPlaying, nextSlide]);

  if (variant === 'ct') {
    return (
      <div className="quote-wrap rv" style={{ marginTop: 'clamp(40px, 4.5vw, 64px)' }}>
        <div
          className="quote-track"
          id="quoteTrack"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {testimonials.map((t, index) => {
            const isOpen = currentIndex === index;
            return (
              <figure
                key={index}
                className={`quote ${isOpen ? 'on' : ''}`}
                style={{
                  gridArea: '1 / 1 / 2 / 2',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  opacity: isOpen ? 1 : 0,
                  visibility: isOpen ? 'visible' : 'hidden',
                  transform: isOpen ? 'none' : 'translateY(16px)',
                  transition: 'opacity 0.4s ease-in-out, transform 0.4s ease-in-out, visibility 0.4s',
                  pointerEvents: isOpen ? 'auto' : 'none'
                }}
              >
                <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="quote-by">
                  <span className="avatar" aria-hidden="true">
                    {t.avatar || (t.name ? t.name.split(' ').map(n => n[0]).join('') : 'CL')}
                  </span>
                  <span className="who">
                    <b>{t.name}</b>
                    <span>{t.meta}</span>
                  </span>
                </figcaption>
              </figure>
            );
          })}
        </div>

        <div className="quote-nav">
          <button
            className="qbtn"
            id="qPrev"
            aria-label="Previous testimonial"
            onClick={() => {
              prevSlide();
              setIsPlaying(false);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" stroke="#0a3d62" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          
          <div className="qdots" aria-label="Choose a testimonial">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`qdot ${currentIndex === index ? 'on' : ''}`}
                onClick={() => {
                  goToSlide(index);
                  setIsPlaying(false);
                }}
                aria-current={currentIndex === index ? 'true' : 'false'}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          <button
            className="qbtn"
            id="qPause"
            aria-label={isPlaying ? 'Pause testimonial rotation' : 'Start testimonial rotation'}
            aria-pressed={!isPlaying ? 'true' : 'false'}
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#0a3d62" aria-hidden="true">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#0a3d62" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            className="qbtn"
            id="qNext"
            aria-label="Next testimonial"
            onClick={() => {
              nextSlide();
              setIsPlaying(false);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 5l7 7-7 7" stroke="#0a3d62" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Home Page (Standard Auditing) Testimonials
  const activeTestimonial = testimonials[currentIndex];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center', width: '100%' }}>
      <div className="testimonial-card">
        <div style={{ color: '#B5652E', fontSize: '18px', letterSpacing: '3px' }}>★★★★★</div>
        <div style={{ fontSize: '18px', fontStyle: 'italic', lineHeight: '1.6', color: '#39362F' }}>
          “{activeTestimonial?.quote}”
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ fontWeight: 800, color: '#26332C', fontSize: '14.5px' }}>
            {activeTestimonial?.name}
          </div>
          <div style={{ fontSize: '12.5px', color: '#8A8377' }}>
            {activeTestimonial?.meta}
          </div>
        </div>
        <button
          onClick={prevSlide}
          className="testimonial-arrow-left"
        >
          ‹
        </button>
        <button
          onClick={nextSlide}
          className="testimonial-arrow-right"
        >
          ›
        </button>
      </div>
      <div style={{ fontSize: '12px', color: '#8A8377' }}>
        {currentIndex + 1} / {testimonials.length}
      </div>
    </div>
  );
};

export default Testimonials;
